import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";

export interface EditorExample {
  slot: number;
  glowId: string | null;
  text: string;
  description: string;
  status: "empty" | "ready" | "unavailable";
}

export interface EditorMission {
  id: number;
  name: string;
  level: string;
  mission: string;
  helpText: string;
  strike: string;
  truths: string;
  door: string;
  doorImage: string;
}
export type EditorFields = Omit<EditorMission, "id" | "level" | "doorImage">;
interface Session {
  authenticated: boolean;
  csrfToken: string;
}
interface Envelope<T> {
  result: T;
}
export const editorKey = ["mission-editor"] as const;
// Editor cookies are separate from player credentials and never go in URLs.
const client = axios.create({ withCredentials: true });
const endpoints = API_ENDPOINTS.editor;

export function editorError(error: unknown) {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      "The editor is unavailable. Please try again."
    );
  }
  return "The editor is unavailable. Please try again.";
}

export function useEditor(missionId?: number) {
  const cache = useQueryClient();
  const session = useQuery({
    queryKey: [...editorKey, "session"],
    queryFn: async () => {
      const result = (await client.get<Envelope<Session>>(endpoints.session))
        .data.result;
      if (!result.authenticated)
        cache.removeQueries({ queryKey: [...editorKey, "missions"] });
      return result;
    },
    retry: false,
    staleTime: 0,
  });
  const missions = useQuery({
    queryKey: [...editorKey, "missions"],
    queryFn: async () => {
      try {
        return (
          await client.get<Envelope<{ missions: EditorMission[] }>>(
            endpoints.missions,
          )
        ).data.result.missions;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          cache.setQueryData([...editorKey, "session"], {
            ...session.data,
            authenticated: false,
          });
          cache.removeQueries({ queryKey: [...editorKey, "missions"] });
        }
        throw error;
      }
    },
    enabled: session.data?.authenticated === true,
    retry: false,
  });
  const examples = useQuery({
    queryKey: [...editorKey, "missions", missionId, "examples"],
    queryFn: async ({ signal }) => {
      try {
        return (
          await client.get<Envelope<{ examples: EditorExample[] }>>(
            endpoints.examples(missionId!),
            { signal },
          )
        ).data.result.examples;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          cache.setQueryData([...editorKey, "session"], {
            ...session.data,
            authenticated: false,
          });
          cache.removeQueries({ queryKey: [...editorKey, "missions"] });
        }
        throw error;
      }
    },
    enabled: session.data?.authenticated === true && missionId != null,
    retry: false,
  });
  const headers = () => ({ "X-CSRFToken": session.data?.csrfToken ?? "" });
  const sendCode = useMutation({
    mutationFn: async (phone: string) => {
      await client.post(endpoints.phoneCode, { phone }, { headers: headers() });
    },
  });
  const verify = useMutation({
    mutationFn: async (code: string) => {
      await client.post(endpoints.session, { code }, { headers: headers() });
    },
    onSuccess: async () => {
      await session.refetch();
    },
  });
  const logout = useMutation({
    mutationFn: async () => {
      await client.delete(endpoints.session, { headers: headers() });
    },
    onSuccess: async () => {
      cache.removeQueries({ queryKey: [...editorKey, "missions"] });
      cache.setQueryData([...editorKey, "session"], {
        ...session.data,
        authenticated: false,
      });
      await session.refetch();
    },
  });
  const save = useMutation({
    mutationFn: async ({
      id,
      fields,
    }: {
      id: number;
      fields: EditorFields;
    }) => {
      try {
        return (
          await client.put<Envelope<{ missions: EditorMission[] }>>(
            endpoints.mission(id),
            fields,
            { headers: headers() },
          )
        ).data.result.missions;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          cache.setQueryData([...editorKey, "session"], {
            ...session.data,
            authenticated: false,
          });
          cache.removeQueries({ queryKey: [...editorKey, "missions"] });
        }
        throw error;
      }
    },
    onSuccess: (rows) => cache.setQueryData([...editorKey, "missions"], rows),
  });
  const uploadDoor = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      try {
        return (
          await client.post<
            Envelope<{ missions: EditorMission[]; updatedIds: number[] }>
          >(endpoints.doorImage(id), form, { headers: headers() })
        ).data.result;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          cache.setQueryData([...editorKey, "session"], {
            ...session.data,
            authenticated: false,
          });
          cache.removeQueries({ queryKey: [...editorKey, "missions"] });
        }
        throw error;
      }
    },
    onSuccess: (result) =>
      cache.setQueryData([...editorKey, "missions"], result.missions),
  });
  const saveExample = useMutation({
    onMutate: ({ id }) =>
      cache.cancelQueries({
        queryKey: [...editorKey, "missions", id, "examples"],
      }),
    mutationFn: async ({
      id,
      slot,
      text,
      glowId,
    }: {
      id: number;
      slot: number;
      text: string;
      glowId: string | null;
    }) => {
      try {
        return (
          await client.put<Envelope<{ examples: EditorExample[] }>>(
            endpoints.example(id, slot),
            { text, glowId },
            { headers: headers() },
          )
        ).data.result.examples;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          cache.setQueryData([...editorKey, "session"], {
            ...session.data,
            authenticated: false,
          });
          cache.removeQueries({ queryKey: [...editorKey, "missions"] });
        }
        throw error;
      }
    },
    onSuccess: (reports, variables) =>
      cache.setQueryData(
        [...editorKey, "missions", variables.id, "examples"],
        reports,
      ),
  });
  return {
    session,
    missions,
    examples,
    sendCode,
    verify,
    logout,
    save,
    uploadDoor,
    saveExample,
  };
}
